import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const STORAGE_KEY = "hifireels_support_sid";

export default function SupportChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hey! I'm Hifireels Support. Ask me anything about Wallet, Reels, Live, AI Studio, gifts, drama series, templates… anything!" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open || !sessionId) return;
    api.get(`/support/chat/history?session_id=${encodeURIComponent(sessionId)}`)
      .then(r => {
        if (r.data?.length) setMsgs(r.data.map(m => ({ role: m.role, content: m.content })));
      }).catch(() => {});
  }, [open, sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, busy]);

  if (!user) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs(m => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/support/chat", { message: text, session_id: sessionId });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY, data.session_id);
      }
      setMsgs(m => [...m, { role: "assistant", content: data.reply || "..." }]);
    } catch (e) {
      setMsgs(m => [...m, { role: "assistant", content: "Sorry, something went wrong. Email hifireelss@gmail.com and we'll help right away." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="support-chat-toggle"
          className="fixed bottom-24 left-3 z-50 w-11 h-11 rounded-full bg-gradient-to-br from-[#A855F7] to-[#FF2A5F] shadow-lg shadow-[#A855F7]/40 flex items-center justify-center active:scale-90 transition"
          aria-label="Open support chat">
          <MessageCircle className="w-5 h-5 text-white"/>
          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#00E5FF] border-2 border-[#0B0F19]"></span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div data-testid="support-chat-panel"
          className="fixed bottom-28 left-3 z-50 w-[min(360px,92vw)] h-[min(540px,72vh)] rounded-3xl bg-[#0B0F19] border border-white/15 shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-[#A855F7]/30 to-[#FF2A5F]/20 border-b border-white/10 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#A855F7] to-[#FF2A5F] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">Hifireels Support</div>
              <div className="text-[10px] text-white/60 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block"></span>
                AI assistant • replies in seconds
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="support-chat-close"
              className="p-1.5 rounded-full hover:bg-white/10 active:scale-90">
              <X className="w-4 h-4"/>
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5" data-testid="support-chat-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`support-msg-${m.role}-${i}`}
                  className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#A855F7] text-white rounded-br-md"
                      : "bg-white/8 border border-white/10 text-white rounded-bl-md"
                  }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="support-typing">
                <div className="bg-white/8 border border-white/10 px-3 py-2 rounded-2xl rounded-bl-md text-sm flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#A855F7]"/>
                  <span className="text-white/60">Thinking…</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-white/10 p-2 flex items-center gap-2">
            <input
              data-testid="support-chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type your question…"
              className="flex-1 px-3 py-2 rounded-full bg-white/5 border border-white/10 outline-none focus:border-[#A855F7] text-sm"
              disabled={busy}
            />
            <button onClick={send} disabled={busy || !input.trim()} data-testid="support-chat-send"
              className="w-9 h-9 rounded-full bg-[#A855F7] flex items-center justify-center disabled:opacity-40 active:scale-90 transition">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4 text-white"/>}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
